<template>
  <div class="min-h-screen flex items-center justify-center px-4 py-16">
    <div class="max-w-lg w-full text-center">
      <h1 class="text-4xl md:text-6xl font-bold mb-4" :class="error.statusCode === 404 ? 'text-orange-600' : 'text-red-600'">
        {{ error.statusCode }}
      </h1>
      <p class="text-xl md:text-2xl font-medium mb-6">
        {{ error.statusCode === 404 ? 'Page Not Found' : 'Something went wrong' }}
      </p>
      <p class="text-gray-500 mb-8">
        {{ error.message || errorMessage }}
      </p>
      
      <div class="space-y-4">
        <!-- Return to home button -->
        <NuxtLink to="/" class="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-md font-medium hover:bg-primary/90 transition-colors">
          Return to Home
        </NuxtLink>
        
        <!-- Go to vehicles page without query -->
        <div v-if="error.statusCode === 500 && error.url && error.url.includes('/vehicles')">
          <p class="text-sm text-gray-500 mt-4 mb-2">
            There was an issue with the vehicles page. Try without any filters:
          </p>
          <NuxtLink to="/vehicles" class="inline-block bg-secondary text-secondary-foreground px-6 py-3 rounded-md font-medium hover:bg-secondary/90 transition-colors">
            View All Vehicles
          </NuxtLink>
        </div>
        
        <!-- Attempt to refresh button -->
        <div v-else-if="error.statusCode === 500">
          <p class="text-sm text-gray-500 mt-4 mb-2">
            You can try refreshing the page:
          </p>
          <button 
            @click="refresh"
            class="inline-block bg-secondary text-secondary-foreground px-6 py-3 rounded-md font-medium hover:bg-secondary/90 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'

const props = defineProps({
  error: {
    type: Object,
    required: true
  }
})

const errorMessage = computed(() => {
  if (props.error.statusCode === 404) {
    return "Sorry, we couldn't find the page you're looking for. It might have been moved or doesn't exist."
  }
  return "Sorry, something went wrong on our end. We're working to fix the issue."
})

// Function to refresh the page
const refresh = () => {
  if (typeof window !== 'undefined') {
    window.location.reload()
  }
}

// Function to recover from error
const clearError = () => {
  clearNuxtError()
  navigateTo(props.error.url || '/')
}

// Handle recovery for specific errors
onMounted(() => {
  // Try to recover from vehicle page errors by adding this to error handling
  if (props.error.statusCode === 500 && props.error.url && props.error.url.includes('/vehicles')) {
    try {
      // Save any query parameters for later recovery
      const queryString = props.error.url.split('?')[1] || '';
      if (queryString) {
        window.__INITIAL_QUERY__ = Object.fromEntries(new URLSearchParams(queryString));
      }
    } catch (e) {
      console.error('Error saving query parameters:', e)
    }
  }
})
</script>